import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import ReusableTable from '../../dashboard/ReusableTable';
import ReusableForm from '../../common/ReusableForm';
import ReusableDeleteDialog from '../../common/ReusableDeleteDialog';
import { getRequest, postRequest } from '../../services/apiService'; // Updated imports
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { WidthFull } from '@mui/icons-material';
import { classNames } from 'primereact/utils';

const SystemManager = () => {
  const toast = useRef(null);
  const [systems, setSystems] = useState([]);
  const [compartments, setCompartments] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [systemToDelete, setSystemToDelete] = useState(null);
  const [system, setSystem] = useState({
    id: null,
    name: "",
    code: "",
    description: "",
    status_db: 1
  });
  
  // Delete confirmation form state
  const [deleteFormData, setDeleteFormData] = useState({
    confirmation: ""
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);

  // Form fields configuration
  const systemFormFields = [
    {
      name: "name",
      label: "System Name",
      type: "text",
      required: true,
      placeholder: "Enter system name",
      helpText: "Enter a unique name for the system"
    },
    {
      name: "code",
      label: "System Code",
      type: "text",
      required: true,
      placeholder: "Enter system code",
      helpText: "Enter a unique code for the system"
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      required: false,
      placeholder: "Enter system description",
      helpText: "Provide a detailed description of the system",
      fullWidth: true
    },
  ];

  // Delete confirmation form fields
  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirm Deletion",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];

 

  // Debounced search effect for table search
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      setLoading(true);
      getRequest(`master/systems/?search=${encodeURIComponent(search)}&page=${currentPage}`)
        .then(response => {
          setSystems(Array.isArray(response.results?.data) ? response.results.data : []);
          setTotalRecords(response.count || 0);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 700); // 700ms debounce

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [search, currentPage]);

  const handleTableSearch = (value) => {
    setSearch(value);
  };

  const fetchSystems = async (page = 1) => {
    setLoading(true);
    try {
      const response = await getRequest(`master/systems/?page=${page}`);
      setSystems(Array.isArray(response.results?.data) ? response.results.data : []);
      setTotalRecords(response.count || 0);
    } catch (error) {
      console.error("Error fetching systems:", error);
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: `Failed to fetch systems: ${error.message}`,
          life: 3000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
  if (!system.name || !system.code || !system.description) {
    if (toast.current) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Name, code, and description are required",
        life: 3000,
      });
    }
    return;
  }

  const payload = { ...system, status_db: system.status_db || 1 };

  try {
    let response = await postRequest("master/systems/", payload);

    // If your postRequest returns a custom object, check for success using a flag
    if (response && response.error) {
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: response.message || "Failed to save system",
          life: 3000,
        });
      }
      return;
    }

    // Success message
    if (toast.current) {
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: system.id ? "System updated successfully" : "System added successfully",
        life: 3000,
      });
    }

    if (system.id) {
      fetchSystems(currentPage);
      setIsUpdateModalOpen(false);
    } else {
      setCurrentPage(1);
      fetchSystems(1);
      setIsAddModalOpen(false);
    }

    // Reset form
    setSystem({
      id: null,
      name: "",
      code: "",
      description: "",
      compartment: "",
      status_db: 1,
    });

  } catch (error) {
    console.error("Error saving system:", error);
    const backendMsg = error?.response?.data?.message || error?.message;
    if (toast.current) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: backendMsg || "An unexpected error occurred",
        life: 3000,
      });
    }
  }
};


  const openDeleteModal = (system) => {
    setSystemToDelete(system);
    setDeleteFormData({ confirmation: "" });
    setShowDeleteModal(true);
  };

  const handleDeleteSubmit = async (e) => {
  if (e) e.preventDefault();

  if (deleteFormData.confirmation !== "DELETE") {
    if (toast.current) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please type 'DELETE' to confirm",
        life: 3000,
      });
    }
    return;
  }

  try {
    // Soft delete using POST with status_db = 3
    const deletePayload = {
      id: systemToDelete.id,
      status_db: 3,
    };

    const response = await postRequest("master/systems/", deletePayload);

    // Check if the API returned an error (assuming postRequest returns parsed JSON)
    if (response && response.error) {
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: response.message || "Failed to delete system",
          life: 3000,
        });
      }
      return;
    }

    // Success case
    if (systems.length === 1 && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    } else {
      fetchSystems(currentPage);
    }

    setShowDeleteModal(false);
    setSystemToDelete(null);
    setDeleteFormData({ confirmation: "" });

    if (toast.current) {
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "System deleted successfully!",
        life: 3000,
      });
    }
  } catch (error) {
    console.error("Error deleting system:", error);
    const backendMsg = error?.response?.data?.message || error?.message;
    if (toast.current) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: backendMsg || "Failed to delete system",
        life: 3000,
      });
    }
  }
};


  const openUpdateModal = (system) => {
    setSystem(system);
    setIsUpdateModalOpen(true);
  };

  const onPageChange = (event) => {
    setCurrentPage(event.page + 1);
    setRowsPerPage(event.rows);
  };

  const columns = [
    { field: 'name', header: 'Name', width:'5%' },
    { field: 'description', header: 'Description' , width:'10%' }
  ];

  const actionItems = [
    {
      label: 'Edit',
      icon: 'pi pi-pencil',
      command: (rowData) => openUpdateModal(rowData)
    },
    {
      label: 'Delete',
      icon: 'pi pi-trash',
      command: (rowData) => openDeleteModal(rowData)
    }
  ];

  return (
    <div className="min-h-screen bg-navy-900 text-white max-w-full">
      <Toast ref={toast} />
      <ToastContainer />
      
      <main className="container mx-auto py-6 px-4">
        <ReusableTable
          title="System Details"
          data={systems}
          setData={setSystems}
          columnFields={columns}
          setAddNewDialogVisible={() => {
            setSystem({
              id: null,
              name: "",
              code: "",
              description: "",
              compartment: "",
              status_db: 1
            });
            setIsAddModalOpen(true);
          }}
          actionMenu={true}
          actionItems={actionItems}
          loading={loading}
          totalRecords={totalRecords}
          rows={rowsPerPage}
          onPageChange={onPageChange}
          first={(currentPage - 1) * rowsPerPage}
          emptyMessage="No system records found. Add new system to get started."
          searchValue={search}
          onSearchChange={handleTableSearch}
        />
      </main>

      {/* Add System Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 text-black">
          <div className={`bg-white rounded-xl shadow-2xl w-full mx-4 animate-fade-in ${systemFormFields.length > 6 ? 'max-w-4xl' : 'max-w-2xl'}`}>
            <ReusableForm
              formData={system}
              setFormData={setSystem}
              fields={systemFormFields}
              onSubmit={handleSave}
              onHide={() => setIsAddModalOpen(false)}
              headerText="Add System"
            />
          </div>
        </div>
      )}

      {/* Update System Modal */}
      {isUpdateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 text-black">
          <div className={`bg-white rounded-xl shadow-2xl w-full mx-4 animate-fade-in ${systemFormFields.length > 6 ? 'max-w-4xl' : 'max-w-2xl'}`}>
            <ReusableForm
              formData={system}
              setFormData={setSystem}
              fields={systemFormFields}
              onSubmit={handleSave}
              onHide={() => setIsUpdateModalOpen(false)}
              headerText="Update System"
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ReusableDeleteDialog
        visible={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={handleDeleteSubmit}
        fields={deleteFormFields}
        formData={deleteFormData}
        setFormData={setDeleteFormData}
        loading={loading}
        title="Delete System"
        description={`This action cannot be undone. All data associated with the system "${systemToDelete?.name}" will be permanently removed. Please type 'DELETE' to confirm.`}
        deleteLabel="Delete"
      />
    </div>
  );
};

export default SystemManager;